/* Spray Calculator Custom Element */
if (!customElements.get('spray-calculator')) {
  class SprayCalculator extends HTMLElement {
    connectedCallback() {
      this.sizes = this._parseSizes();
      this.inner = this.querySelector('.m-spray-calculator__inner');
      this.countEl = this.querySelector('.m-spray-calculator__count');
      this.descEl = this.querySelector('.m-spray-calculator__desc');

      // Initial render based on whatever variant is already selected
      setTimeout(() => this._update(), 100);

      // Listen for option pill/swatch clicks
      document.addEventListener('click', (e) => {
        if (e.target.closest('.m-product-option--node')) {
          setTimeout(() => this._update(), 50);
        }
      });

      // Listen for select dropdowns
      document.addEventListener('change', (e) => {
        if (e.target.closest('.m-product-option')) {
          setTimeout(() => this._update(), 50);
        }
      });

      // Fallback: watch URL changes (theme updates URL on variant change)
      let lastSearch = location.search;
      const urlObserver = setInterval(() => {
        if (location.search !== lastSearch) {
          lastSearch = location.search;
          this._update();
        }
      }, 300);
      this._urlObserver = urlObserver;
    }

    disconnectedCallback() {
      clearInterval(this._urlObserver);
    }

    _parseSizes() {
      // Format per line: "10ml: 25 to 30 sprays | Optional description"
      const raw = (this.dataset.sprayData || '').trim();
      if (!raw) return [];

      return raw.split('\n').reduce((acc, line) => {
        line = line.trim();
        if (!line) return acc;

        // Split on first colon to get size key and the rest
        const colonIdx = line.indexOf(':');
        if (colonIdx === -1) return acc;

        const sizeVal = line.slice(0, colonIdx).trim().toLowerCase();
        const rest = line.slice(colonIdx + 1).trim(); // e.g. "25 to 30 sprays | description"

        // Split rest on pipe to separate spray range from description
        const pipeIdx = rest.indexOf('|');
        const sprayPart = (pipeIdx === -1 ? rest : rest.slice(0, pipeIdx)).trim();
        const desc = pipeIdx === -1 ? '' : rest.slice(pipeIdx + 1).trim();

        // Parse "25 to 30 sprays", "25-30 sprays", "25 à 30 vaporisations", etc.
        // Language-agnostic: just grab the first two numbers, whatever separates them.
        const nums = sprayPart.match(/(\d+)\D+(\d+)/);
        if (!nums) return acc;

        acc.push({
          value: sizeVal,
          min: parseInt(nums[1]),
          max: parseInt(nums[2]),
          desc
        });
        return acc;
      }, []);
    }

    // Reads the currently selected value of every product option on the page,
    // tagged with that option's raw (non-customized) name.
    _getAllSelectedValues() {
      const results = [];
      document.querySelectorAll('.m-product-option').forEach((container) => {
        const inner = container.querySelector('[data-option-name]');
        const rawOptionName = (inner?.dataset.optionName || '').toLowerCase();

        let value = null;
        const activeBtn = container.querySelector('[aria-pressed="true"], .is-selected, .selected, button.active');
        if (activeBtn) {
          value = activeBtn.value || activeBtn.textContent;
        } else {
          const checkedInput = container.querySelector('input:checked');
          if (checkedInput) {
            value = checkedInput.value;
          } else {
            const select = container.querySelector('select');
            if (select) value = select.value;
          }
        }

        if (value) results.push({ rawOptionName, value: value.toLowerCase().trim() });
      });
      return results;
    }

    // Matches a value against the parsed spray-size list, regardless of exact wording.
    _matchSize(value) {
      const normalise = str => (str || '').replace(/\s+/g, '').toLowerCase();
      const normValue = normalise(value);
      return this.sizes.find(s => {
        const normVal = normalise(s.value);
        return normValue === normVal || normValue.includes(normVal) || normVal.includes(normValue);
      });
    }

    _update() {
      const optionName = (this.dataset.optionName || '').toLowerCase();
      const selectedValues = this._getAllSelectedValues();

      // Prefer the option whose raw name relates to the configured "option name" setting
      // (if any), but don't require it: fall back to checking every option's value, so
      // the spray-data keys work no matter what's typed in that field.
      let match = null;
      if (optionName) {
        const named = selectedValues.find(({ rawOptionName }) =>
          rawOptionName && (rawOptionName.includes(optionName) || optionName.includes(rawOptionName))
        );
        if (named) match = this._matchSize(named.value);
      }

      if (!match) {
        for (const { value } of selectedValues) {
          match = this._matchSize(value);
          if (match) break;
        }
      }

      if (match) {
        this.countEl.textContent = `~${match.min}–${match.max} sprays`;
        this.descEl.textContent = match.desc;
        this.inner.style.display = 'flex';
      } else {
        this.inner.style.display = 'none';
      }
    }
  }
  customElements.define('spray-calculator', SprayCalculator);
}

/* Upsell Products Slider */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-upsell-track]').forEach(track => {
    const wrap = track.closest('.m-product-upsell__slider-wrap');
    const prevBtn = wrap && wrap.querySelector('[data-upsell-prev]');
    const nextBtn = wrap && wrap.querySelector('[data-upsell-next]');
    const cards = track.querySelectorAll('.m-product-upsell__card');
    if (!cards.length) return;

    let current = 0;
    const total = cards.length;

    const getVisible = () => window.innerWidth >= 768 ? 2 : 1;

    const goTo = (idx) => {
      const visible = getVisible();
      const max = Math.max(0, total - visible);
      current = Math.max(0, Math.min(idx, max));
      const cardWidth = cards[0].offsetWidth + parseInt(getComputedStyle(track).gap || 0);
      track.style.transform = `translateX(-${current * cardWidth}px)`;
      if (prevBtn) prevBtn.disabled = current === 0;
      if (nextBtn) nextBtn.disabled = current >= max;
    };

    if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));

    // Reset on resize
    window.addEventListener('resize', () => goTo(current));
    goTo(0);
  });
});
